import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTasksStore } from '@/stores/tasks';
import { useContextBundlesStore } from '@/stores/contextBundles';
import { useUiStore } from '@/stores/ui';
import { ContextBundle, ContextItem } from '@/core/state-shape';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Pin, PinOff, XCircle, CheckCircle, FolderGit2, Loader2 } from 'lucide-react';

const TokenMeter = ({ bundle }: { bundle: ContextBundle | undefined }) => {
    if (!bundle) return null;
    const { tokensUsed, budget } = bundle;
    const percentage = budget.hard > 0 ? (tokensUsed / budget.hard) * 100 : 0;
    const isOverBudget = tokensUsed > budget.hard;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Token Budget</CardTitle>
            </CardHeader>
            <CardContent>
                <Progress value={percentage} className={isOverBudget ? '[&>div]:bg-destructive' : ''} />
                <div className={`flex justify-between text-sm mt-2 ${isOverBudget ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                    <span>{tokensUsed.toLocaleString()} / {budget.hard.toLocaleString()} tokens used</span>
                    {isOverBudget && <span>Over budget!</span>}
                </div>
            </CardContent>
        </Card>
    );
};

export default function ContextReviewPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);

    const taskId = searchParams.get('taskId');

    useEffect(() => {
        setHeaderTitle('Context Review');
    }, [setHeaderTitle]);

    // Stores
    const { byId: tasksById, patch: patchTask } = useTasksStore();
    const { byId: bundlesById, build: buildBundleAction, patch: patchBundle } = useContextBundlesStore();

    const task = taskId ? tasksById[taskId] : undefined;
    const bundleId = task?.contextBundleId;
    const bundle = bundleId ? bundlesById[bundleId] : undefined;
    
    const handleBuildBundle = useCallback(async () => {
        if (!taskId) return;
        setLoading(true);
        try {
            const newBundleId = await buildBundleAction(taskId);
            // Link the new bundle to the task
            patchTask({
                byId: {
                    ...tasksById,
                    [taskId]: {
                        ...tasksById[taskId],
                        contextBundleId: newBundleId,
                    }
                }
            });
        } catch (error) {
            console.error("Failed to build context bundle", error);
        } finally {
            setLoading(false);
        }
    }, [taskId, buildBundleAction, patchTask, tasksById]);

    useEffect(() => {
        // Automatically build a bundle if one doesn't exist for the task
        if (taskId && task && !bundleId && !loading) {
            handleBuildBundle();
        }
    }, [taskId, task, bundleId, loading, handleBuildBundle]);


    const handleItemToggle = (itemId: string, field: 'pinned' | 'excluded') => {
        if (!bundle) return;
        
        const newItems = bundle.items.map(item => {
            if (item.id === itemId) {
                const isCurrentlySet = !!item[field];
                const newItem = { ...item, [field]: !isCurrentlySet };
                // Pinning and excluding are mutually exclusive
                if (field === 'pinned' && newItem.pinned) newItem.excluded = false;
                if (field === 'excluded' && newItem.excluded) newItem.pinned = false;
                return newItem;
            }
            return item;
        });

        patchBundle({
            byId: {
                ...bundlesById,
                [bundle.id]: { ...bundle, items: newItems },
            },
        });
    };

    const handleLockAndContinue = () => {
        if (!bundle || !taskId) return;
        
        patchBundle({
            byId: {
                ...bundlesById,
                [bundle.id]: { ...bundle, locked: true },
            },
        });
        
        navigate(`/pages/progress/?taskId=${taskId}`);
    };
    
    if (!taskId) {
        return <div className="p-8 text-center text-muted-foreground">Task ID not specified.</div>
    }

    if (!task) {
        return <div className="p-8 text-center text-muted-foreground">Loading task...</div>
    }
    
    if (loading) {
        return <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Building context bundle...</div>
    }

    if (!bundle) {
        return (
            <div className="p-8 text-center text-muted-foreground">
                <p>No context bundle found for this task.</p>
                <Button onClick={handleBuildBundle} className="mt-4">Build Bundle</Button>
            </div>
        );
    }


    return (
        <div className="max-w-4xl mx-auto p-4 lg:p-6 space-y-6">
            <header className="space-y-2 hidden md:block">
                <h1 className="text-2xl font-bold">Context Review</h1>
                <p className="text-muted-foreground">
                    Finalize the context for task: <span className="font-semibold text-foreground">{task.title || taskId}</span>
                </p>
            </header>

            <TokenMeter bundle={bundle} />

            <Card>
                <CardHeader>
                    <CardTitle>Context Items ({bundle.items.length})</CardTitle>
                    <CardDescription>Pin items to guarantee inclusion, or exclude them to save token space.</CardDescription>
                </CardHeader>
                <CardContent>
                    <ul className="space-y-2">
                        {bundle.items.map((item: ContextItem) => (
                            <li key={item.id} className="flex items-center justify-between p-3 rounded-md border bg-background hover:bg-accent">
                                <div className="flex items-center gap-3 truncate">
                                    <FolderGit2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <div className="truncate">
                                        <p className="font-mono text-sm truncate" title={item.ref}>{item.ref}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {item.type} &middot; ~{item.estimate.tokens} tokens
                                            {item.pinned && <Badge variant="secondary" className="ml-2">Pinned</Badge>}
                                            {item.excluded && <Badge variant="outline" className="ml-2">Excluded</Badge>}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => handleItemToggle(item.id, 'pinned')} title={item.pinned ? "Unpin" : "Pin"} disabled={bundle.locked}>
                                        {item.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleItemToggle(item.id, 'excluded')} title={item.excluded ? "Include" : "Exclude"} disabled={bundle.locked}>
                                        <XCircle className={`h-4 w-4 ${item.excluded ? 'text-destructive' : ''}`} />
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </CardContent>
            </Card>

            <footer className="flex justify-end pt-4">
                <Button size="lg" onClick={handleLockAndContinue} disabled={bundle.locked || bundle.tokensUsed > bundle.budget.hard}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {bundle.locked ? 'Bundle Locked' : 'Lock Bundle & Continue'}
                </Button>
            </footer>
        </div>
    );
}